import { Form, Input, Space } from 'antd';
import { PricingComponent } from 'components/Billing/pricing';
import { ConfirmBoxContainer } from 'containers/ConfirmBox';
import GenTaxImage from 'assets/images/gen-tax-img.svg';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from 'routes';
import {
  ContactUsMessageType,
  Currency,
  SubscriptionPlan,
  AccountType,
} from 'store/api/enums';
import { turnModalOn } from 'store/app/actions';
import { WindowType } from 'store/app/enums';
import { ModalData } from 'store/app/types';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import {
  fetchPlanPricingDetailsRequest,
  fetchSubscriptionTypesRequest,
  sendContactUsEmailRequest,
  setCurrentPlan,
} from 'store/billing/actions';
import {
  getBillingLoading,
  getSelectedPlan,
  getSubscriptionPlanTypes,
} from 'store/billing/selectors';
import { checkPageLoaded } from 'utils/check-page-loaded';
import { isPlanUuid } from 'utils/subscriptionPlanType';
import EmailLogo from 'assets/images/Emails-amico@3x.png';
import { CenterLogo } from 'components/Custom/logo';

const { TextArea } = Input;

export const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [conatctUsForm] = Form.useForm();
  const [showPage, setShowPage] = useState(false);
  const selectedPlan = useSelector(getSelectedPlan);
  const currentUser = useSelector(getAuthUser);
  const subscriptionPlanTypes = useSelector(getSubscriptionPlanTypes);
  const isLoading = useSelector(getBillingLoading);
  const userLicence = useSelector(getAuthUserLicence);

  // Check if user is owner, if not redirect to main page
  React.useEffect(() => {
    if (
      currentUser &&
      AccountType[currentUser.accountType] !== AccountType.OWNER
    ) {
      navigate(routes.main);
    }
  }, [currentUser, navigate]);

  const activePlane = (
    plan: SubscriptionPlan,
    selectedCurrency: Currency,
    price: string,
    licenseId: string
  ) => {
    if (!licenseId?.trim()) {
      dispatch(fetchSubscriptionTypesRequest());
      return;
    }
    dispatch(setCurrentPlan(plan, selectedCurrency, price));
    if (isPlanUuid(licenseId)) {
      dispatch(fetchPlanPricingDetailsRequest(licenseId));
    } else {
      dispatch(fetchSubscriptionTypesRequest());
    }
    navigate(`/pricing/checkout/${encodeURIComponent(licenseId)}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  React.useEffect(() => {
    setShowPage(true);
    if (subscriptionPlanTypes.length == 0) {
      dispatch(fetchSubscriptionTypesRequest());
    }
    currentUser && checkPageLoaded(location.pathname, routes.pricing.root);
    conatctUsForm.resetFields();
  }, []);

  const renderContactUsWindow = (isEnterprise: boolean) => {
    conatctUsForm.setFieldValue('message', '');
    const modalData: ModalData = {
      type: WindowType.WARNING,
      onClose: () => conatctUsForm.setFieldValue('message', ''),
      title: isEnterprise ? (
        <div style={{ display: 'block' }}>
          <CenterLogo
            width="200px"
            height="200px"
            src={EmailLogo}
            style={{ paddingBottom: 15 }}
          />
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {t('billing.reach-out-title')}
          </span>
          <br />
          <br />
          <span style={{ fontSize: 14, fontWeight: 400 }}>
            {t('billing.enterprise-contact-intro')}
            <br /> <br />
            {t('billing.leave-note-before-send')}
          </span>
        </div>
      ) : (
        <div style={{ display: 'block' }}>
          <CenterLogo
            width="200px"
            height="200px"
            src={EmailLogo}
            style={{ paddingBottom: 15 }}
          />
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {t('billing.reach-out-title')}
          </span>
          <br />
          <br />
          <span style={{ fontSize: 14, fontWeight: 400 }}>
            {t('billing.plan-help-contact-intro')}
            <br /> <br />
            {t('billing.leave-note-before-send')}
          </span>
        </div>
      ),
      component: (
        <ConfirmBoxContainer
          callback={
            () => {
              dispatch(
                sendContactUsEmailRequest({
                  message: conatctUsForm.getFieldValue('message'),
                  subject: isEnterprise
                    ? ContactUsMessageType.ENTERPRISE
                    : ContactUsMessageType.HELP,
                })
              );
              conatctUsForm.setFieldValue('message', '');
            }
            //navigateToExternalPage('https://generate.tax/contact/')
          }
          showCancelBtn={false}
          okText={t('billing.request-more-info')}
          description={
            <Form form={conatctUsForm}>
              <Space direction="vertical" style={{ width: '100%', rowGap: 30 }}>
                <Form.Item name="message">
                  <TextArea
                    rows={4}
                    style={{ width: '100%', marginTop: '20px' }}
                    //onChange={(e) => setMessageValue(e.target.value)}
                    //value={messageValue}
                  />
                </Form.Item>
              </Space>
            </Form>
          }
        />
      ),
    };

    dispatch(turnModalOn(modalData));
  };

  const downgradePlan = (typeId: string) => {
    const modalData: ModalData = {
      type: WindowType.WARNING,
      title: t('billing.downgrade-confirm-title'),
      icon: <img src={GenTaxImage} alt="" />,
      component: (
        <ConfirmBoxContainer
          callback={
            () => {
              navigate(`/pricing/checkout/${typeId}`);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            //navigateToExternalPage('https://generate.tax/contact/')
          }
          showCancelBtn={true}
          okText={t('billing.downgrade-confirm-ok')}
          description={
            <>
              {t('billing.downgrade-confirm-description')}
              <br />
              {t('billing.change-starts-next-cycle')}
            </>
          }
        />
      ),
    };

    dispatch(turnModalOn(modalData));
  };

  return (
    <React.Fragment>
      {showPage && (
        <>
          <Helmet>
            <title>{`${t('navigation.pricing')} | ${process.env.PROJECT_NAME}`}</title>
            <meta name="description" content={t('billing.meta-description-pricing')} />
          </Helmet>
          <PricingComponent
            selectedPlan={selectedPlan}
            activePlane={activePlane}
            subscriptionPlanTypes={subscriptionPlanTypes.reverse()}
            isLoading={isLoading}
            renderContactUsWindow={renderContactUsWindow}
            downgradePlan={downgradePlan}
            userLicence={userLicence}
          />
        </>
      )}
    </React.Fragment>
  );
};
